import { Navigate, Route, Routes } from 'react-router-dom';
import { RequireAuth } from './auth/RequireAuth.tsx';
import { ToastProvider } from './components/Toast.tsx';
import { SessionProvider } from './hooks/useSession.tsx';
import { Shell } from './layout/Shell.tsx';
import { Activate } from './pages/Activate.tsx';
import { Billing } from './pages/Billing.tsx';
import { BillingTopupPage } from './pages/BillingTopupPage.tsx';
import { BillingTopupReturnPage } from './pages/BillingTopupReturnPage.tsx';
import { Dashboard } from './pages/Dashboard.tsx';
import { ForgotPassword } from './pages/ForgotPassword.tsx';
import { Invite } from './pages/Invite.tsx';
import { Login } from './pages/Login.tsx';
import { NotFound } from './pages/NotFound.tsx';
import { Pricing } from './pages/Pricing.tsx';
import { Register } from './pages/Register.tsx';
import { Requests } from './pages/Requests.tsx';
import { ResetPassword } from './pages/ResetPassword.tsx';
import { RootRedirect } from './pages/RootRedirect.tsx';
import { Settings } from './pages/Settings.tsx';
import { AccessDocs } from './pages/access/Docs.tsx';
import { AccessKeys } from './pages/access/Keys.tsx';
import { AccessStatus } from './pages/access/Status.tsx';
import { TeamAudit } from './pages/team/Audit.tsx';
import { TeamMembers } from './pages/team/Members.tsx';
import { TeamPermissions } from './pages/team/Permissions.tsx';
import { TeamUnits } from './pages/team/Units.tsx';
import { Usage } from './pages/Usage.tsx';
import { VerifyEmail } from './pages/VerifyEmail.tsx';

/**
 * Top-level tenant-console route table.
 *
 * Unauthenticated routes render bare cards on a solid backdrop
 * (`Login`, `Register`, `VerifyEmail`, `ForgotPassword`,
 * `ResetPassword`). Authenticated routes live inside a nested
 * `<RequireAuth><Shell>` tree.
 *
 * The QueryClientProvider lives in `main.tsx` so tests can mount a
 * fresh client per render without re-creating the whole tree.
 */
export function App(): JSX.Element {
  return (
    <SessionProvider>
      <ToastProvider>
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          {/* Invite-code activation (epic #1475) — public, and public on
              purpose: it is the entry point a salesperson hands out. */}
          <Route path="/activate" element={<Activate />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/password-reset" element={<ResetPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/invite/:token" element={<Invite />} />
          <Route
            path="/*"
            element={
              <RequireAuth>
                <Shell>
                  <Routes>
                    <Route path="/dashboard" element={<Dashboard />} />
                    {/* Access split (epic #897 PR 5): three real routes. */}
                    <Route path="/access" element={<Navigate to="/access/keys" replace />} />
                    <Route path="/access/keys" element={<AccessKeys />} />
                    <Route path="/access/docs" element={<AccessDocs />} />
                    <Route path="/access/status" element={<AccessStatus />} />
                    {/* Legacy access aliases redirect to the canonical routes. */}
                    <Route path="/access/api-keys" element={<Navigate to="/access/keys" replace />} />
                    <Route path="/access/base-url" element={<Navigate to="/access/docs" replace />} />
                    <Route path="/access/model-availability" element={<Navigate to="/access/status" replace />} />
                    <Route path="/keys" element={<Navigate to="/access/keys" replace />} />
                    <Route path="/usage" element={<Usage />} />
                    {/* Team rebuild (epic #897 PR 7): four real routes. */}
                    <Route path="/team" element={<Navigate to="/team/members" replace />} />
                    <Route path="/team/members" element={<TeamMembers />} />
                    <Route path="/team/permissions" element={<TeamPermissions />} />
                    <Route path="/team/units" element={<TeamUnits />} />
                    <Route path="/team/audit" element={<TeamAudit />} />
                    {/* Legacy team aliases redirect to the canonical routes. */}
                    <Route path="/team/invitations" element={<Navigate to="/team/members" replace />} />
                    <Route path="/team/model-permissions" element={<Navigate to="/team/permissions" replace />} />
                    <Route path="/team/org-units" element={<Navigate to="/team/units" replace />} />
                    <Route path="/organization" element={<Navigate to="/team/members" replace />} />
                    <Route path="/requests" element={<Requests />} />
                    <Route path="/pricing" element={<Pricing />} />
                    <Route path="/billing" element={<Billing />} />
                    <Route path="/billing/topup" element={<BillingTopupPage />} />
                    <Route path="/billing/topup/return" element={<BillingTopupReturnPage />} />
                    <Route path="/billing/pricing" element={<Pricing />} />
                    <Route path="/settings" element={<Settings />} />
                    <Route path="/settings/account" element={<Settings initialSection="account" />} />
                    <Route path="/settings/security" element={<Settings initialSection="security" />} />
                    <Route path="/settings/technical" element={<Settings initialSection="technical" />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Shell>
              </RequireAuth>
            }
          />
        </Routes>
      </ToastProvider>
    </SessionProvider>
  );
}
